
import React, { useState, useEffect } from 'react';
import { Database, Link2, Search, CheckCircle2, ChevronRight, Globe, FileText, Trash2 } from 'lucide-react';
import ManageDataModal from '../components/ManageDataModal';
import CreateKBModal from '../components/CreateKBModal';
import { apiUrl } from '../../utils/api';

interface KnowledgeBase {
  id: string;
  name: string;
  type: string;
  count: string;
  status: string;
  icon: React.ReactNode;
}

const DataIntegrations: React.FC = () => {
  const [selectedKB, setSelectedKB] = useState<{ id: string; name: string } | null>(null);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchKnowledgeBases = async () => {
    try {
      const response = await fetch(apiUrl('/api/knowledge-bases/'));
      if (response.ok) {
        let data = await response.json();

        // Handle both array and object response formats
        if (data.value && Array.isArray(data.value)) {
          data = data.value;
        }

        if (!Array.isArray(data)) {
          data = [data];
        }

        const formattedKBs = data.map((kb: any) => ({
          id: kb.knowledge_base_id,
          name: kb.name,
          type: kb.source_type,
          count: `${kb.file_count} files`,
          status: kb.status === 'indexed' ? 'Synced' : 'Indexing',
          icon: <FileText />,
        }));
        setKnowledgeBases(formattedKBs);
      } else {
        console.error('Failed to fetch KBs:', response.status);
        setKnowledgeBases([]);
      }
    } catch (error) {
      console.error('Failed to fetch knowledge bases:', error);
      setKnowledgeBases([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKnowledgeBases();
  }, []);

  const handleUploadSuccess = () => {
    fetchKnowledgeBases();
  };

  const handleDeleteKB = async (kbId: string, kbName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${kbName}"? This action cannot be undone.`)) {
      return;
    }

    setDeleting(kbId);
    try {
      const response = await fetch(apiUrl(`/api/knowledge-bases/${kbId}`), {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete KB: ${response.statusText}`);
      }

      setKnowledgeBases((prev) => prev.filter((kb) => kb.id !== kbId));
      alert('Knowledge Base deleted successfully');
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete Knowledge Base');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Data & Integrations</h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Connect your internal knowledge and external SaaS tools to the agent ecosystem.</p>
      </div>

      <div className="space-y-6">
        <h3 className="text-lg font-bold text-slate-900 dark:text-white">Knowledge Bases (RAG)</h3>
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-500 dark:text-slate-400">Loading knowledge bases...</p>
          </div>
        ) : knowledgeBases.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700">
            <p className="text-slate-500 dark:text-slate-400 mb-4">No knowledge bases yet</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 shadow-lg shadow-blue-500/20 transition-all"
            >
              Create First Knowledge Base
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {knowledgeBases.map((kb, i) => (
              <div key={i} className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col justify-between group cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition-colors relative">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-slate-50 dark:bg-slate-900/50 text-slate-400 group-hover:bg-blue-50 dark:group-hover:bg-blue-900/40 group-hover:text-blue-600 dark:group-hover:text-blue-400 rounded-2xl transition-colors">
                      {React.cloneElement(kb.icon as React.ReactElement<any>, { size: 24 })}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${kb.status === 'Synced' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 animate-pulse'
                        }`}>
                        {kb.status}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteKB(kb.id, kb.name);
                        }}
                        disabled={deleting === kb.id}
                        className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-50"
                        title="Delete Knowledge Base"
                      >
                        <Trash2 size={16} className="text-red-600 dark:text-red-400" />
                      </button>
                    </div>
                  </div>
                  <h4 className="font-bold text-slate-900 dark:text-white mb-1">{kb.name}</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-6">{kb.type} • {kb.count}</p>
                </div>
                <button
                  onClick={() => setSelectedKB({ id: kb.id, name: kb.name })}
                  className="w-full py-2.5 bg-slate-50 dark:bg-slate-700/50 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 group-hover:bg-blue-600 group-hover:text-white transition-all flex items-center justify-center gap-2"
                >
                  Manage Data <ChevronRight size={14} />
                </button>
              </div>
            ))}
            <button
              onClick={() => setShowCreateModal(true)}
              className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-2xl flex flex-col items-center justify-center p-6 text-slate-400 hover:border-blue-300 dark:hover:border-blue-500 hover:text-blue-500 transition-all group">
              <Plus size={32} className="mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-bold">New Knowledge Base</span>
            </button>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm transition-colors">
        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6">Channel Integrations</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-6">
          {[
            { name: 'WhatsApp', icon: 'https://cdn-icons-png.flaticon.com/512/733/733585.png', active: true },
            { name: 'Slack', icon: 'https://cdn-icons-png.flaticon.com/512/2111/2111615.png', active: true },
            { name: 'Discord', icon: 'https://cdn-icons-png.flaticon.com/512/3670/3670157.png', active: false },
            { name: 'Twilio', icon: 'https://cdn-icons-png.flaticon.com/512/5969/5969164.png', active: true },
            { name: 'Outlook', icon: 'https://cdn-icons-png.flaticon.com/512/732/732200.png', active: false },
            { name: 'Zendesk', icon: 'https://cdn-icons-png.flaticon.com/512/5968/5968962.png', active: true },
          ].map((brand, i) => (
            <div key={i} className="flex flex-col items-center group cursor-pointer">
              <div className={`w-14 h-14 rounded-2xl border-2 p-3 transition-all ${brand.active ? 'border-emerald-100 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-100 dark:border-slate-700 grayscale hover:grayscale-0 grayscale opacity-40 hover:opacity-100'}`}>
                <img src={brand.icon} alt={brand.name} className="w-full h-full object-contain" />
              </div>
              <span className={`text-[11px] font-bold mt-2 ${brand.active ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-500'}`}>{brand.name}</span>
            </div>
          ))}
        </div>
      </div>

      <ManageDataModal
        isOpen={!!selectedKB}
        onClose={() => setSelectedKB(null)}
        knowledgeBaseName={selectedKB?.name || ''}
        knowledgeBaseId={selectedKB?.id || ''}
        onUploadSuccess={handleUploadSuccess}
      />

      <CreateKBModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          fetchKnowledgeBases();
        }}
      />
    </div>
  );
};

const Plus: React.FC<{ size?: number, className?: string }> = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

// Added default export to resolve "has no default export" error in App.tsx
export default DataIntegrations;
